import { assert } from '@ember/debug';
import Component from '@glimmer/component';
import type { WithBoundArgs } from '@glint/template';
import type { FormData, FormKey } from 'ember-headless-form';
import type { HeadlessFormComponentSignature } from 'ember-headless-form/components/headless-form';
import { type UserData } from '../../../types.ts';
import type RadioField628 from './radio';
import RadioField from './radio.gts';

export interface WrFormRadioGroupFieldComponentSignature<
  DATA extends UserData,
  KEY extends FormKey<FormData<DATA>> = FormKey<FormData<DATA>>
> {
  Element: HTMLFieldSetElement;
  Args: {
    label: string;
    name: KEY;
    form: HeadlessFormComponentSignature<DATA, unknown>['Blocks']['default'][0];
    onClick?: () => void;
    variant?: 'toggle-buttons';
  };
  Blocks: {
    default: [
      Radio: WithBoundArgs<
        typeof RadioField628,
        'name' | 'selectedValue' | 'onChange' | 'variant'
      >
    ];
  };
}

export default class WrFormRadioGroupFieldComponent<
  DATA extends UserData,
  KEY extends FormKey<FormData<DATA>> = FormKey<FormData<DATA>>
> extends Component<WrFormRadioGroupFieldComponentSignature<DATA, KEY>> {
  formatValue = (value: unknown) => {
    assert(
      `Only string or boolean values are expected for ${
        this.args.name
      }, but you passed ${typeof value}`,
      value == null || typeof value === 'string' || typeof value === 'boolean'
    );

    return value;
  };

  setValue = (fieldSetValue: (value: DATA[KEY]) => void) => {
    return (value: string) => {
      if (value == 'true') {
        fieldSetValue(true as DATA[KEY]);
      } else if (value == 'false') {
        fieldSetValue(false as DATA[KEY]);
      } else {
        fieldSetValue(value as DATA[KEY]);
      }
      this.args.onClick?.();
    };
  };

  get radioVariant() {
    return this.args.variant == 'toggle-buttons' ? 'toggle-button' : undefined;
  }

  <template>
    {{! prettier-ignore }}
    <@form.Field @name={{@name}} as |field|>
      {{! @glint-expect-error }}
      {{#let (component RadioField name=@name selectedValue=(this.formatValue field.value) onChange=(this.setValue field.setValue) variant=this.radioVariant ) as |Radio| }}
        <fieldset
          role='radiogroup'
          aria-describedby={{field.errorId}}
          aria-invalid={{if field.isInvalid 'true'}}
          aria-required='true'
          ...attributes
        >
        <legend>{{@label}}</legend>

        <div class="flex">
          {{yield Radio}}
        </div>
        </fieldset>
      {{/let}}
    </@form.Field>
  </template>
}
